import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, includes, isArray } from 'lodash';
import { compose, withProps, withHandlers, withStateHandlers } from 'recompose';
import { Expand } from '@sparefoot/react-core';
import { withShowHide } from '@sparefoot/react-core/decorators';
import { getFilterValueForAmenity } from 'utils/search/parseAmenityFromPath';

import { Button } from 'components/core/Button';
import { Checkbox } from 'components/core/Checkbox';
import { RadioGroup, RadioInput } from 'components/core/RadioInput';
import { VehicleGuide } from 'components/unit/VehicleGuide';

import Caret from 'components/svg/Caret';

import './FilterGroup.scss';

export const enhance = compose(
	withStateHandlers(
		() => ({
			viewGuide: false,
		}),
		{
			handelViewGuide:
				({ viewGuide }) =>
				() => ({
					viewGuide: !viewGuide,
				}),
		},
	),
	withShowHide('filters', (props) => !!props.showing),
	withHandlers({
		handleOnChange:
			({ allowMultiple, onFilter, searchParams, delayFiltering }) =>
			({ target }) => {
				const { checked, value, name } = target;
				const params = get(searchParams, name, []);
				const filterUpdate = {};
				if (allowMultiple) {
					filterUpdate[name] = checked
						? [...params, value]
						: params.filter((param) => param !== value);
				} else {
					filterUpdate[name] = checked ? value : null;
				}

				// Clear the subPageType param when unchecking the filter
				// that corresponds to the current amenity landing page
				// context. This prevents the selector from re-injecting
				// the filter on the next render.
				if (
					!checked &&
					name === 'amenities' &&
					searchParams.subPageType &&
					getFilterValueForAmenity(searchParams.subPageType) === value
				) {
					filterUpdate.subPageType = null;
				}

				onFilter(filterUpdate, delayFiltering);
			},
	}),
	withProps(({ options }) => ({
		defaultValue: get(
			options.find((option) => option.default),
			'value',
		),
	})),
);

/**
 * FilterGroup is a collapsable group of radio or checkbox inputs.
 */
export function FilterGroup({
	defaultValue,
	label,
	filterName,
	filtersShowing,
	filterType,
	facetCounts,
	onToggleFilters,
	searchParams,
	handleOnChange,
	options,
	modal,
	listKey,
	handelViewGuide,
	viewGuide,
	useNewSearch,
}) {
	return (
		<div
			className={classnames('filter-group', {
				expanded: filtersShowing,
				'new-search': useNewSearch,
			})}
		>
			<Button
				onClick={onToggleFilters}
				className="filter-group-label"
			>
				<Caret /> {label}
			</Button>
			<Expand showing={filtersShowing}>
				<div>
					<div className="filter-options">
						{filterType === 'radio' && (
							<RadioGroup
								value={
									searchParams[options[0]?.name] ||
									defaultValue
								}
								onChange={handleOnChange}
							>
								{options.map((option) => (
									<RadioInput
										key={option.value}
										id={`${listKey}-${option.name}-${option.value}`}
										name={option.name}
										label={option.label}
										value={option.value}
									/>
								))}
							</RadioGroup>
						)}
						{useNewSearch && filterType === 'selector' && (
							<div className="filter-button-group">
								{options.map((option) => {
									// Validate that searchParams value is actually in the options
									const validValues = options.map(
										(opt) => opt.value,
									);
									const currentValue =
										searchParams[filterName];
									const selectedValue = validValues.includes(
										currentValue,
									)
										? currentValue
										: defaultValue;

									const isSelected =
										selectedValue === option.value;

									return (
										<Button
											secondary
											key={option.value}
											className={classnames(
												'filter-selector-button',
												{
													active: isSelected,
												},
											)}
											onClick={() =>
												handleOnChange({
													target: {
														value: option.value,
														name: option.name,
														checked: true,
													},
												})
											}
										>
											{option.label}
										</Button>
									);
								})}
							</div>
						)}
						{filterType !== 'radio' &&
							filterType !== 'selector' &&
							options.map((option) => {
								const isInObject =
									JSON.stringify(facetCounts).includes(
										option?.value,
									) ||
									JSON.stringify(facetCounts).includes(
										option?.facetName,
									);
								const isZero =
									facetCounts[option?.value] === 0 ||
									facetCounts[option?.facetName] === 0;
								const isChecked =
									searchParams[option.name] ===
										`${option.value}` ||
									(isArray(searchParams[option.name]) &&
										includes(
											searchParams[option.name],
											`${option.value}`,
										));

								return (
									<Checkbox
										disabled={
											filterName === 'amenities' &&
											isInObject &&
											isZero &&
											!isChecked
										}
										key={`${option.name}-${option.value}`}
										id={`${listKey}-${option.name}-${option.value}`}
										label={`${option.label}`}
										name={option.name}
										value={option.value}
										onChange={handleOnChange}
										checked={isChecked}
									/>
								);
							})}
					</div>
					{modal && (
						<div>
							<div className="filter-modal">
								<Button
									link
									onClick={handelViewGuide}
									className="modal-button"
								>
									{viewGuide
										? modal.titleToClose
										: modal.title}
								</Button>
							</div>
							{viewGuide && (
								<Fragment>
									<div className="guide-container">
										<VehicleGuide
											vehicleGuides={modal.content}
										/>
									</div>
									<div className="filter-modal">
										<Button
											link
											onClick={handelViewGuide}
											className="modal-button"
										>
											{modal.titleToClose}
										</Button>
									</div>
								</Fragment>
							)}
						</div>
					)}
				</div>
			</Expand>
		</div>
	);
}

FilterGroup.propTypes = {
	listKey: PropTypes.string.isRequired,
	defaultValue: PropTypes.string,
	filterName: PropTypes.string.isRequired,
	filtersShowing: PropTypes.bool.isRequired,
	filterType: PropTypes.oneOf(['radio', 'checkbox', 'selector']),
	handleOnChange: PropTypes.func.isRequired,
	label: PropTypes.string,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string.isRequired,
			value: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			facetName: PropTypes.string,
		}),
	),
	modal: PropTypes.shape({
		title: PropTypes.string,
		titleToClose: PropTypes.string,
		content: PropTypes.object,
	}),
	onToggleFilters: PropTypes.func.isRequired,
	searchParams: PropTypes.object,
	facetCounts: PropTypes.object,
	handelViewGuide: PropTypes.func,
	viewGuide: PropTypes.bool,
	useNewSearch: PropTypes.bool,
};

export default enhance(FilterGroup);
